import { _get } from "../../api/util"

export type Formatter =
    | "core"
    | "string"
    | "date"
    | "time"
    | "float"
    | "integer"
    | "boolean"
    | "enum"
    | "custom"
    | "modifier"
    | "array"

export type SourceModel =
    | "core"
    | "company"
    | "cohort"
    | "cost_code"
    | "timecard_owner"
    | "employee"
    | "project"
    | "group"
    | "timecard"
    | "employee_timecard"
    | "shift_extra"
    | "shift_extra_entry"
    | "timekeeping_entry"

export type RequiredSetting =
    | "date_group_daily"
    | "date_group_weekly"
    | "date_group_all_time"
    | "time_as_rows"
    | "time_as_columns"
    | "not_grouped"
    | "grouped"

export type ExpressionObject = {
    source: string
    token: string
    description: string
    qualified_token: string
    is_alias: boolean
    formatter?: Formatter
    source_model?: SourceModel
    required_settings?: RequiredSetting[]
}

export type TokenSuggestion = {
    token: string
    index: number
    expression: ExpressionObject
}

export class Tokenizer {
    static tokenData: ExpressionObject[] = []
    static filenameTokenData: ExpressionObject[] = []
    static requirementSettings: string[]

    static fetchTokenData = (options: Record<string, any>): void => {
        _get("/api/v4/tk_export_formats/get_grammar_data/", options)
            .then(returnedData => {
                Tokenizer.tokenData = returnedData as ExpressionObject[]
            })
            .catch(() => {
                Tokenizer.tokenData = []
            })
    }

    static fetchFilenameTokenData = (options: Record<string, any>): void => {
        _get("/api/v4/tk_export_formats/get_filename_grammar_data/", options)
            .then(returnedData => {
                Tokenizer.filenameTokenData = returnedData as ExpressionObject[]
            })
            .catch(() => {
                Tokenizer.filenameTokenData = []
            })
    }

    static setRequirements = (values: string[]): void => {
        Tokenizer.requirementSettings = [...values]
    }

    /**
     * Get the token suggestions
     * @param token The token to get suggestions for
     * @param tokenSource where to get token data from
     * @returns A list of child token suggestions
     */
    static getTokenSuggestions = (
        token?: string,
        tokenSource: "tokenData" | "filenameTokenData" = "tokenData"
    ): TokenSuggestion[] => {
        const tokenData = tokenSource === "tokenData" ? Tokenizer.tokenData : Tokenizer.filenameTokenData

        const checkRequirements = (e: ExpressionObject) => {
            const requiredSettings = e.required_settings || []
            const sourceModel = e.source_model
            const hasRequiredSettings = requiredSettings.length > 0

            if (!hasRequiredSettings) {
                return true
            }

            const fulfillsRequirements = requiredSettings.every(setting => {
                if (!sourceModel) {
                    return false
                }
                if (setting === "date_group_weekly") {
                    return (
                        Tokenizer.requirementSettings.includes("date_group_weekly") ||
                        // Daily grouping implies weekly grouping
                        Tokenizer.requirementSettings.includes("date_group_daily")
                    )
                }
                return Tokenizer.requirementSettings.includes(setting)
            })

            return fulfillsRequirements
        }

        const convertAlias = (e: ExpressionObject) => {
            if (e.is_alias) {
                const tokenMatch = tokenData.find(t => t.qualified_token === e.source)
                if (tokenMatch) {
                    return { original: e, aliased: tokenMatch }
                } else {
                    throw new Error(`Alias ${e.source} not found`)
                }
            }
            // If the token is not an alias, both are the same
            return { original: e, aliased: e }
        }

        const matchSearch = ({ original, aliased }: { original: ExpressionObject; aliased: ExpressionObject }) => {
            if (!token) {
                return true
            }
            // The token name we want to match is with the original un-aliased token, so if they type
            // the name of an alias, we return the token that matches what they typed.
            if (original.qualified_token.toLowerCase().indexOf(token.toLowerCase()) >= 0) {
                return true
            }
            // The description we want to match is with the aliased token, so we get the full
            // description and documentation from the token it points to, rather than the token we're picking
            if (aliased.description.toLowerCase().includes(token.toLowerCase())) {
                return true
            }
            return false
        }

        return (
            tokenData
                // Convert aliases to the actual token
                .map(convertAlias)
                // Filter out tokens that don't meet the requirements (e.g. date_group_weekly or time_as_rows)
                // We use the aliased token for this, since that's where the requirements are defined
                .filter(e => checkRequirements(e.aliased))
                // Filter out tokens that don't match the search
                .filter(matchSearch)
                // Sort the remaining tokens alphabetically
                .sort((a, b) => a.aliased.token.localeCompare(b.aliased.token))
                // And format the output to match the expected format
                .map((e, index) => ({
                    // The token value we return for selection should be the bit that matches the search
                    token: e.original.qualified_token,
                    index: index,
                    // And we keep the aliased token for documentation purposes
                    expression: e.aliased,
                }))
        )
    }
}
